import React, { useState, useCallback } from "react";
import ApiURl from "../controllers/Api";
import {
  FiLock,
  FiShield,
  FiPhone,
  FiKey,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiAlertTriangle,
} from "react-icons/fi";

// Small reusable input wrapper with left icon + optional right button
const Field = React.memo(({
  label,
  icon,
  right,
  hint,
  errorText,
  children,
}) => (
  <div className="space-y-2">
    <label className="block text-sm font-semibold text-black">{label}</label>
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
        {icon}
      </div>
      {children}
      {right}
    </div>
    {hint && !errorText && <p className="text-xs text-gray-500">{hint}</p>}
    {errorText && <p className="text-xs font-medium text-red-600">{errorText}</p>}
  </div>
));

const ChangePassword = () => {
  const userId = localStorage.getItem("user_id");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [useOtpMethod, setUseOtpMethod] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Calculate directly without useMemo to prevent focus issues
  const isPasswordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const isWeakPassword = newPassword.length > 0 && newPassword.length < 6;

  const resetForm = useCallback(() => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setOtp("");
    setPhoneNumber("");
    setOtpSent(false);
  }, []);

  const handleSendOtp = async () => {
    setError("");
    setMessage("");

    if (!phoneNumber) {
      setError("Please enter your phone number.");
      return;
    }

    if (!userId) {
      setError("User is not logged in.");
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("userId", userId);
      payload.append("phoneNumber", phoneNumber.replace(/\D/g, ""));

      const response = await fetch(`${ApiURl}/SendPasswordChangeOtp.php`, {
        method: "POST",
        body: payload,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `Request failed with status ${response.status}`);
      }

      if (data.success) {
        setMessage(`OTP has been sent to ${phoneNumber}`);
        setOtpSent(true);
      } else {
        setError(data.message || "Failed to send OTP. Please try again.");
      }
    } catch (err) {
      console.error("OTP sending error:", err);
      setError(err.message || "Failed to process your request. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!userId) {
      setError("User is not logged in.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    if (useOtpMethod) {
      if (!phoneNumber) {
        setError("Please enter your phone number.");
        return;
      }
      if (!otp) {
        setError("Please enter the OTP.");
        return;
      }
    } else {
      if (!currentPassword) {
        setError("Please enter your current password.");
        return;
      }
    }

    setLoading(true);

    try {
      const response = await fetch(`${ApiURl}/changepassword.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: useOtpMethod ? null : currentPassword,
          newPassword,
          confirmPassword,
          userId,
          otp: useOtpMethod ? otp : null,
          phoneNumber: useOtpMethod ? phoneNumber : null,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (data.message) {
        setMessage(data.message);
        resetForm();
      } else {
        setError(data.error || "An error occurred while changing the password.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to connect to the server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Memoize toggle handlers to prevent re-renders
  const toggleShowCurrent = useCallback(() => setShowCurrent(s => !s), []);
  const toggleShowNew = useCallback(() => setShowNew(s => !s), []);
  const toggleShowConfirm = useCallback(() => setShowConfirm(s => !s), []);

  const handleCurrentPasswordChange = useCallback((e) => setCurrentPassword(e.target.value), []);
  const handleNewPasswordChange = useCallback((e) => setNewPassword(e.target.value), []);
  const handleConfirmPasswordChange = useCallback((e) => setConfirmPassword(e.target.value), []);
  const handlePhoneNumberChange = useCallback((e) => setPhoneNumber(e.target.value), []);
  const handleOtpChange = useCallback((e) => setOtp(e.target.value), []);

  const handleToggleCurrentMethod = useCallback(() => {
    setUseOtpMethod(false);
    setError("");
    setMessage("");
    setOtpSent(false);
    setOtp("");
  }, []);

  const handleToggleOtpMethod = useCallback(() => {
    setUseOtpMethod(true);
    setError("");
    setMessage("");
  }, []);

  return (
    <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] px-4 py-10">
      <div className="mx-auto w-full max-w-lg">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 ring-1 ring-blue-100">
            <FiShield className="text-blue-700" size={22} />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-black">Change Password</h1>
          <p className="mt-2 text-sm text-gray-600">
            Use your current password or verify with OTP to update your password securely.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-gray-200 p-3">
            <button
              type="button"
              onClick={handleToggleCurrentMethod}
              className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition
                ${
                  !useOtpMethod
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-gray-50 text-black hover:bg-gray-100"
                }`}
            >
              Current Password
            </button>

            <button
              type="button"
              onClick={handleToggleOtpMethod}
              className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition
                ${
                  useOtpMethod
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-gray-50 text-black hover:bg-gray-100"
                }`}
            >
              OTP Verification
            </button>
          </div>

          {/* Alerts */}
          {(message || error) && (
            <div className="p-4">
              {message && (
                <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  <FiCheckCircle className="mt-0.5 shrink-0" />
                  <div>{message}</div>
                </div>
              )}
              {error && (
                <div className="mt-3 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  <FiAlertTriangle className="mt-0.5 shrink-0" />
                  <div>{error}</div>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {!useOtpMethod ? (
              <Field
                label="Current Password"
                icon={<FiLock size={18} />}
                right={
                  <button
                    type="button"
                    onClick={toggleShowCurrent}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-black"
                    aria-label={showCurrent ? "Hide password" : "Show password"}
                  >
                    {showCurrent ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                }
              >
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={handleCurrentPasswordChange}
                  className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-12 py-3 text-black
                             outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                  placeholder="Enter your current password"
                  required
                />
              </Field>
            ) : (
              <div className="space-y-5">
                <Field
                  label="Phone Number"
                  icon={<FiPhone size={18} />}
                  hint="We will send a 6-digit OTP to this number."
                >
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    disabled={otpSent}
                    className={`w-full rounded-xl border pl-10 pr-4 py-3 text-black outline-none transition
                      ${
                        otpSent
                          ? "border-gray-200 bg-gray-50 text-gray-600"
                          : "border-gray-200 bg-white focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                      }`}
                    placeholder="Enter your phone number"
                    required
                  />
                </Field>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <Field label="OTP Code" icon={<FiKey size={18} />}>
                    <input
                      type="text"
                      value={otp}
                      onChange={handleOtpChange}
                      className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-3 text-black
                                 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                      placeholder="Enter OTP"
                      required
                    />
                  </Field>

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSent || loading}
                    className={`rounded-xl px-4 py-3 text-sm font-semibold transition
                      ${
                        otpSent || loading
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                      }`}
                  >
                    {otpSent ? "OTP Sent" : loading ? "Sending..." : "Get OTP"}
                  </button>
                </div>
              </div>
            )}

            <Field
              label="New Password"
              icon={<FiLock size={18} />}
              hint="Minimum 6 characters."
              errorText={isWeakPassword ? "Password is too short." : ""}
              right={
                <button
                  type="button"
                  onClick={toggleShowNew}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-black"
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              }
            >
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={handleNewPasswordChange}
                className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-12 py-3 text-black
                           outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                placeholder="Enter your new password"
                required
              />
            </Field>

            <Field
              label="Confirm New Password"
              icon={<FiLock size={18} />}
              errorText={isPasswordMismatch ? "Passwords do not match." : ""}
              right={
                <button
                  type="button"
                  onClick={toggleShowConfirm}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-black"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              }
            >
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className={`w-full rounded-xl border bg-white pl-10 pr-12 py-3 text-black outline-none transition
                  ${
                    isPasswordMismatch
                      ? "border-red-300 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                  }`}
                placeholder="Confirm your new password"
                required
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition
                ${
                  loading
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 shadow-sm"
                } focus:outline-none focus:ring-4 focus:ring-blue-100`}
            >
              {loading ? "Processing..." : "Change Password"}
            </button>

            <div className="text-center text-xs text-gray-500">
              Tip: Use a strong password with letters, numbers, and symbols.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
